/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 23/05/2025 - 02:10:12
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 23/05/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useState, useEffect } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeError, PaymentIntent } from "@stripe/stripe-js";
import Navbar from "../shared/navbar";
import Footer from "../shared/footer";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../shared/loader";
import { api } from "../../api/transaction";
import { toast } from "react-toastify";
import axios, { AxiosError } from "axios";

interface VoucherDetails {
  code: string;
  type: 'percentage' | 'fixed';
  value: string;
}

interface AnalysisLocationState {
  clientSecret: string;
  orderId: string;
  paymentIntentId: string;
  amount: number;
  service: string;
  analysisOrderId: string;
  userId: string;
  email: string;
  userName: string;
}

const AnalysisCheckoutForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [couponID, setCouponId] = useState("");
  const [error, setError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [apply, setApply] = useState("Apply");
  const [originalAmount, setOriginalAmount] = useState(199);
  const [finalAmount, setFinalAmount] = useState(199);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [appliedVoucher, setAppliedVoucher] = useState<VoucherDetails | null>(null);
  const [isFreeOrder, setIsFreeOrder] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as AnalysisLocationState;

  // Validate analysis payment data on load
  useEffect(() => {
    if (!locationState?.paymentIntentId || !locationState?.orderId) {
      console.error("No analysis payment data found in location state");
      setError("Analysis payment setup is incomplete. Please try again.");
    }
  }, [locationState]);

  // Handle analysis payment submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
  
    if (!stripe || !elements) {
      console.error("Stripe or elements not available");
      setError("Payment cannot be processed. Please refresh the page and try again.");
      setIsLoading(false);
      return;
    }
  
    try {
      // console.log("Confirming analysis payment...");
      
      // Confirm the payment
      const { paymentIntent, error }: { paymentIntent?: PaymentIntent; error?: StripeError } =
        await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: "https://pv-glarecheck.com/analysisorders", 
          },
          redirect: "if_required", 
        });
  
      if (error) {
        console.error("Error confirming analysis payment:", error);
        setError(`Payment error: ${error.message}`);
        setIsLoading(false);
        return;
      }
      
      if (paymentIntent && paymentIntent.status === "succeeded") {
        // console.log("Analysis payment succeeded:", paymentIntent);
        
        try {
          // Update analysis order status in MongoDB via API
          
          const statusUpdateData = {
            transactionId: paymentIntent.id,
            amount: paymentIntent.amount / 100, // Convert cents to euros
            voucherCode: appliedVoucher?.code || null,
            discountAmount: discountAmount || 0
          };
          
          // console.log("Sending status update data:", statusUpdateData);
          
          await api.updateAnalysisOrderStatus(locationState.orderId, statusUpdateData);

          // console.log("Analysis order status updated in MongoDB successfully");
          
          // Clean up and show success
          localStorage.removeItem("analysisClientSecret");
          toast.success("Analysis payment successful! Redirecting...");
          
          // Navigate to success page with order data
          setTimeout(() => {
            navigate("/analysissuccess", {
              state: {
                orderId: locationState?.orderId,
                amount: (paymentIntent.amount / 100).toFixed(2),
                voucherApplied: appliedVoucher?.code,
                transactionId: paymentIntent.id
              }
            });
          }, 1500);
          
        } catch (updateError) {
          console.error("Error updating analysis order status:", updateError);
          // Even if status update fails, payment succeeded, so show success
          toast.success("Payment successful! Your analysis order has been processed.");
          setTimeout(() => {
            navigate("/analysissuccess", {
              state: {
                orderId: locationState?.orderId,
                amount: (paymentIntent.amount / 100).toFixed(2),
                voucherApplied: appliedVoucher?.code,
                transactionId: paymentIntent.id
              }
            });
          }, 1500);
        }
      } else {
        // console.log("Payment status:", paymentIntent?.status);
        setError("Payment not completed. Please try again.");
      }    
    } catch (err) {
      console.error("Unexpected analysis payment error:", err);
      setError("An unexpected error occurred during payment. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Apply voucher code to analysis order
  const ApplyCoupon = async () => {
    if (!couponID) {
      toast.warning("Please enter the voucher code");
      return;
    }
    
    if (!locationState?.paymentIntentId) {
      toast.error("Analysis payment setup is incomplete. Please refresh the page.");
      return;
    }
    
    setCouponLoading(true);
    
    try {

      
      const response = await api.applyVoucher({
        voucherCode: couponID,
        paymentIntentId: locationState.paymentIntentId,
      });
      
      const data = response.data;
      
      // Update the state with all the details
      setFinalAmount(data.finalAmount / 100);
      setOriginalAmount(data.originalAmount / 100);
      setDiscountAmount(data.discountAmount / 100);
      setAppliedVoucher(data.voucher);
      
      // Check if analysis order is completely free
      if (data.finalAmount === 0) {
        setIsFreeOrder(true);
        toast.success(`Voucher ${data.voucher.code} applied - Your analysis service is FREE!`);
      } else {
        // Regular discount
        setApply(`Applied: ${data.voucher.value} off`);
        toast.success(`Voucher ${data.voucher.code} applied successfully!`);
      }
    } catch (error) {
      console.error("Voucher application error:", error);
      setApply("Apply");
      
      // Enhanced error handling
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        
        if (axiosError.response?.data) {
          const data = axiosError.response.data as any;
          
          if (data.message) {
            toast.error(data.message);
          } else if (data.error?.message) {
            toast.error(data.error.message);
          } else if (axiosError.response?.status === 404) {
            toast.error("Invalid voucher code");
          } else {
            toast.error("Failed to apply voucher. Please try again.");
          }
        } else {
          toast.error("Connection error. Please try again.");
        }
      } else {
        toast.error("An unexpected error occurred");
      }
    } finally {
      setCouponLoading(false);
    }
  };

  // Process a free analysis order (100% discount)
  const completeAnalysisOrderAsFree = async () => {
    if (!appliedVoucher) {
      toast.error("No voucher applied");
      return;
    }
    
    if (!locationState?.orderId) {
      toast.error("Analysis order ID is missing. Please try again.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      
      const response = await api.completeFreeOrder({
        voucherCode: appliedVoucher.code,
        originalAmount: originalAmount * 100,
        projectId: locationState.orderId,
      });
      
      try {
        // Update analysis order status in MongoDB via API for free order

        
        const freeOrderStatusData = {
          transactionId: `free_analysis_${Date.now()}`,
          amount: 0, // Free order amount is 0
          voucherCode: appliedVoucher.code,
          discountAmount: originalAmount // Full original amount as discount
        };

        
        await api.updateAnalysisOrderStatus(locationState.orderId, freeOrderStatusData);
        
      } catch (updateError) {
        console.error("Error updating free analysis order status:", updateError);
        // Continue even if status update fails
      }
      
      // Show success and redirect
      toast.success("Your free analysis order has been processed! Redirecting...");
      setTimeout(() => {
        navigate("/analysissuccess", {
          state: {
            orderId: locationState?.orderId,
            amount: "0.00",
            voucherApplied: appliedVoucher.code,
            transactionId: `free_analysis_${Date.now()}`
          }
        });
      }, 1500);
      
    } catch (error) {
      console.error("Error processing free analysis order:", error);
      toast.error("Failed to process your free analysis order. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Navbar />
      <div className="container mt-4 pt-4">
        <div className="text-center">
          <h4 style={{ color: "#fffff", fontWeight: "500" }}>
            Analysis Service - Total Cost: €{finalAmount}
          </h4>
          
          {/* Show service type indicator */}
          <div className="mb-2">
            <span className="badge bg-primary">Analysis as a Service</span>
          </div>
          
          {/* Show discount info if a voucher is applied */}
          {discountAmount > 0 && (
            <div className="discount-info mt-2">
              <p className="original-price text-muted">
                <span style={{ textDecoration: "line-through" }}>€{originalAmount}</span>
                {" "}
                <span className="text-success">
                  {appliedVoucher ? appliedVoucher.value : `€${discountAmount} off`}
                </span>
              </p>
            </div>
          )}
        </div>
        
        <form className="container checkoutForm" onSubmit={handleSubmit}>
          {/* Only show payment element if the final amount is not 0 */}
          {finalAmount > 0 && <PaymentElement />}

          {finalAmount === 0 ? (
            <button
              onClick={completeAnalysisOrderAsFree}
              type="button"
              className="d-flex justify-content-center align-items-center mb-5 payBtn btn-success"
              style={{ height: "35px" }}
              disabled={isLoading}
            >
              {isLoading ? <Loader /> : "GET ANALYSIS FOR FREE"}
            </button>
          ) : (
            <button
              className="d-flex justify-content-center align-items-center mb-5 payBtn"
              style={{ height: "35px" }}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? <Loader /> : `PAY €${finalAmount} FOR ANALYSIS`}
            </button>
          )}
        </form>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default AnalysisCheckoutForm;